import React, { useEffect, useState } from 'react';

import { orderBy } from 'lodash';

import moment from 'moment';
import clsx from 'clsx';
import { getTimeZoneAbbr } from '../helpers/timezones';

import classes from './WatchDogReport.module.css';

const WatchDogReport = React.forwardRef(({ report, ...props }, ref) => {

    const [equipmentList, setEquipmentList] = useState([]);

    useEffect(() => {
        if (report.EntityDetails.length) {
            const sorted = orderBy(report.EntityDetails, ['Score'], ['asc']);
            setEquipmentList(sorted);
        }
    }, [report]);

    const convertDate = date => {
        const zone = moment.tz.guess();
        return moment(date).format('YYYY-MM-DD h:mm A') + ' ' + getTimeZoneAbbr(moment(date), zone);
    };

    const renderLogo = report => {
        if (report.CompanyLogoUrl) {
            return (
                <img 
                    src={report.CompanyLogoUrl} 
                    alt={report.CompanyName} 
                    height="5rem" 
                    crossOrigin="anonymous"
                />
            );
        }

        return (
            <h1>{report.CompanyName}</h1>
        );
    };

    const renderTotalGoodEquipment = equipment => {
        let count = 0;

        equipment.forEach(item => {
            if (item.Score * 100 >= 75) {
                count = count + 1;
            }
        });

        return count;
    };

    const renderTotalOkEquipment = equipment => {
        let count = 0;

        equipment.forEach(item => {
            if (item.Score * 100 < 75 && item.Score * 100 >= 50) {
                count = count + 1;
            }
        });

        return count;
    };

    const renderTotalBadEquipment = equipment => {
        let count = 0;

        equipment.forEach(item => {
            if (item.Score * 100 < 50) {
                count = count + 1;
            }
        });

        return count;
    };

    const renderAverageEquipmentScore = equipment => {
        if (!equipment.length) return 'N/A';

        let total = 0;

        equipment.forEach(item => {
            total = total + (item.Score * 100);
        });

        return Math.round(((total / equipment.length) + Number.EPSILON) * 100) / 100;
    };

    const renderMinEquipmentScore = equipment => {
        if (!equipment.length) return 'N/A';

        const scores = equipment.map(item => (Math.round(((item.Score * 100) + Number.EPSILON) * 100) / 100));
        return Math.min(...scores);
    };

    const renderMaxEquipmentScore = equipment => {
        if (!equipment.length) return 'N/A';

        const scores = equipment.map(item => (Math.round(((item.Score * 100) + Number.EPSILON) * 100) / 100));
        return Math.max(...scores);
    };

    const renderValue = number => {
        const value = number * 100;

        return (Math.round((value + Number.EPSILON) * 100) / 100);
    };

    return (
        <React.Fragment>
            <style>
                {
                    `
                        @font-face {
                            font-family: "DejaVu Sans";
                            src: url("https://kendo.cdn.telerik.com/2017.2.621/styles/fonts/DejaVu/DejaVuSans.ttf") format("truetype");
                        }

                        @font-face {
                            font-family: "DejaVu Sans";
                            font-weight: bold;
                            src: url("https://kendo.cdn.telerik.com/2017.2.621/styles/fonts/DejaVu/DejaVuSans-Bold.ttf") format("truetype");
                        }

                        @font-face {
                            font-family: "DejaVu Sans";
                            font-style: italic;
                            src: url("https://kendo.cdn.telerik.com/2017.2.621/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf") format("truetype");
                        }

                        @font-face {
                            font-family: "DejaVu Sans";
                            font-weight: bold;
                            font-style: italic;
                            src: url("https://kendo.cdn.telerik.com/2017.2.621/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf") format("truetype");
                        }
                    `
                }
            </style>
            <div ref={ref} className={classes.WatchDogReport}>
                <div className={classes.Header}>
                    {renderLogo(report)}
                    <div>
                        <h2>Site Performance Rankings</h2>
                    </div>
                </div>
                <div className={classes.Timestamp}>{convertDate(report.targetDateStart)} TO {convertDate(report.targetDateEnd)}</div>
                <div className={classes.PerformanceRankingSummary}>
                    <div className={classes.SummaryHeader}>
                        Performance Ranking Summary
                    </div>
                    <div className={classes.SummaryBody}>
                        <div className={classes.SummaryTotal}>
                            Total Equipment
                            <div>
                                <span className={classes.Number}>{report.EntityDetails.length}</span>
                            </div>
                        </div>
                        <div className={classes.SummaryBodyLabel}>
                            Total Good Equipment
                            <div className={classes.SummaryBlock}>
                                <div className={classes.GoodIcon} />
                                <span className={classes.Number}>{renderTotalGoodEquipment(report.EntityDetails) || 0}</span>
                            </div>
                        </div>
                        <div className={classes.SummaryBodyLabel}>
                            Total Ok Equipment
                            <div className={classes.SummaryBlock}>
                                <div className={classes.OkIcon} />
                                <span className={classes.Number}>{renderTotalOkEquipment(report.EntityDetails) || 0}</span>
                            </div>
                        </div>
                        <div className={classes.SummaryBodyLabel}>
                            Total Bad Equipment
                            <div className={classes.SummaryBlock}>
                                <div className={classes.BadIcon} />
                                <span className={classes.Number}>{renderTotalBadEquipment(report.EntityDetails) || 0}</span>
                            </div>
                        </div>
                        <div className={classes.SummaryBodyLabel}>
                            Avg Equipment Score
                            <div className={classes.SummaryBlock}>
                                <div className={renderAverageEquipmentScore(report.EntityDetails) >= 75 ? classes.GoodIcon : renderAverageEquipmentScore(report.EntityDetails) >= 50 ? classes.OkIcon : classes.BadIcon} />
                                <span className={classes.Number}>{renderAverageEquipmentScore(report.EntityDetails) || 0}</span>
                            </div>
                        </div>
                        <div className={classes.SummaryBodyLabel}>
                            Min Equipment Score
                            <div className={classes.SummaryBlock}>
                                <div className={renderMinEquipmentScore(report.EntityDetails) >= 75 ? classes.GoodIcon : renderMinEquipmentScore(report.EntityDetails) >= 50 ? classes.OkIcon : classes.BadIcon} />
                                <span className={classes.Number}>{renderMinEquipmentScore(report.EntityDetails) || 0}</span>
                            </div>
                        </div>
                        <div className={classes.SummaryBodyLabel}>
                            Max Equipment Score
                            <div className={classes.SummaryBlock}>
                                <div className={renderMaxEquipmentScore(report.EntityDetails) >= 75 ? classes.GoodIcon : renderMaxEquipmentScore(report.EntityDetails) >= 50 ? classes.OkIcon : classes.BadIcon} />
                                <span className={classes.Number}>{renderMaxEquipmentScore(report.EntityDetails) || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="page-break" style={{ display: 'none' }} />
                <div className={classes.EquipmentList}>
                    <div className={classes.NameLabel}>
                        Equipment Name
                    </div>
                    <div className={classes.ScoreLabel}>
                        Equipment Score
                    </div>
                    {
                        equipmentList.length ?
                            equipmentList.map((item, index) => {
                                return (
                                    <React.Fragment>
                                        <div className={clsx(classes.NameColumn, index%2 === 0 ? classes.OddColumn : classes.EvenColumn)}>
                                            <div className={item.Score * 100 >= 75 ? classes.TableGood : item.Score * 100 >= 50 ? classes.TableOk : classes.TableBad} />
                                            <div>{item.EntityName}</div>
                                        </div>
                                        <div className={clsx(classes.ScoreColumn, index % 2 === 0 ? classes.OddColumn : classes.EvenColumn)}>
                                            <div className={classes.StatusBar}>
                                                <div style={{ width: item.Score * 100 }} className={item.Score * 100 >= 75 ? classes.StatusGood : item.Score * 100 >= 50 ? classes.StatusOk : classes.StatusBad} />
                                            </div>
                                            <div>{item.Score === null ? 'No Data' : renderValue(item.Score)}</div>
                                        </div>
                                    </React.Fragment>
                                );
                            })
                            :
                            <div className={clsx(classes.NoEquipment, classes.OddColumn)}>
                                No Equipment Returned
                            </div>
                    }
                </div>
            </div>
        </React.Fragment>
    );
});

export default WatchDogReport;
